import React, {
  memo,
  useCallback,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';
import Shield from './ui/error';

import { sleep } from './utils';
import { colors } from './styles';

import Lazy from './ui/lazy';
import { default as ChartBase } from './chart';

import Loading from './ui/loading';

const props_check = props => {
  if (props.borderWidth > 20)
    throw new Error('Provide a smaller value for @borderWidth@');
  if (props.height >= 900)
    throw new Error('Provide a smaller value for @height@');
  if (props.variant === 'updown' && props.values.length !== 2)
    throw new Error('Updown accepts 2 values only');
  if (
    (props.median && props.variant === 'dots') ||
    (props.median && props.variant === 'pie') ||
    (props.median && props.variant === 'stack')
  )
    throw new Error(`${props.variant} do not accept <Median/>`);
  if (props.variant === 'series') {
    const integrity = props.values[0].length === props.values[1].length;
    if (!integrity) {
      throw new Error(`All ${props.variant} need to be of the same length`);
    }
  }
};

const Base = props => {
  props_check(props);

  const { chartBg, height, lazy = false } = props;

  const [loading, set_loading] = useState(true);
  const [waiting, set_waiting] = useState(() => lazy);
  const [dimensions, set_dimensions] = useState(false);

  const element = useRef(null);
  useLayoutEffect(() => {
    if (!element.current) {
      return;
    }
    on_resize();
    if (typeof ResizeObserver === 'function') {
      let observer = new ResizeObserver(() => on_resize());
      observer.observe(element.current);

      return () => {
        if (!observer) {
          return;
        }

        observer.disconnect();
        observer = null;
      };
    }
  }, [element.current]);

  const on_resize = useCallback(() => {
    if (!element.current) {
      return;
    }
    const rect = element.current.getBoundingClientRect();
    if (rect) {
      set_dimensions({
        width: rect.width,
      });
      sleep(300).then(() => set_loading(false));
    }
  }, [element.current]);

  const stop_waiting = () => set_waiting(false);

  return (
    <>
      <div ref={element} style={{ position: 'relative' }}>
        <Shield h={height} fill={chartBg}>
          {waiting && <Lazy h={height} fill={chartBg} cb={stop_waiting} />}
          {!waiting && loading && <Loading h={height} />}
          {!waiting && dimensions.width && (
            <ChartBase
              {...props}
              chart_height={height}
              chart_width={dimensions.width}
            />
          )}
        </Shield>
      </div>
    </>
  );
};

const props_equal = (prevProps, nextProps) => {
  return prevProps.values === nextProps.values;
  // when the function returns true, component will not be re-rendered
};
const BaseMemoized = memo(props => <Base {...props} />, props_equal);

// PUBLIC API

const Chart = {
  Area: props => <BaseMemoized variant="area" {...props} />,
  Bar: props => <BaseMemoized variant="bar" rounded {...props} />,
  Cube: props => <BaseMemoized variant="bar" {...props} />,
  Cubes: props => <BaseMemoized variant="cubes" {...props} />,
  Dots: props => <BaseMemoized variant="dots" {...props} />,
  Extruded: props => <BaseMemoized variant="extruded" {...props} />,
  Line: props => <BaseMemoized variant="line" {...props} />,
  Peak: props => <BaseMemoized variant="peak" {...props} />,
  Pie: props => <BaseMemoized variant="pie" {...props} />,
  Pyramid: props => <BaseMemoized variant="pyramid" {...props} />,
  Series: props => <BaseMemoized variant="series" {...props} />,
  Slanted: props => <BaseMemoized variant="slanted" {...props} />,
  Stack: props => (
    <BaseMemoized variant="stack" boundaryX={0} boundaryY={-10} {...props} />
  ),
  SkyScraper: props => <BaseMemoized variant="skyscraper" {...props} />,
  UpDown: props => <BaseMemoized variant="updown" {...props} />,
};

const value_prop = {
  id: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
};

Base.propTypes = {
  boundaryX: PropTypes.number,
  boundaryY: PropTypes.number,
  chartBg: PropTypes.string,
  chunkBy: PropTypes.number,
  shades: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  height: PropTypes.number,
  title: PropTypes.string,
  borderWidth: PropTypes.number,
  borderColor: PropTypes.string,
  topSpace: PropTypes.number,
  style: PropTypes.any,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  values: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape(value_prop)),
    PropTypes.arrayOf(PropTypes.array),
  ]).isRequired,

  variant: PropTypes.oneOf([
    'area',
    'bar',
    'dots',
    'cubes',
    'extruded',
    'line',
    'peak',
    'pie',
    'pyramid',
    'series',
    'skyscraper',
    'slanted',
    'stack',
    'updown',
  ]),
};

Base.defaultProps = {
  boundaryX: -10,
  boundaryY: 0,
  chartBg: 'transparent',
  chunkBy: 10,
  shades: colors.accent,
  height: 300,
  title: 'Untitled chart',
  borderWidth: 2,
  borderColor: 'transparent',
  topSpace: 30,
};

export default Chart;
